import React, { useState } from 'react';
import { Container, Row, Col, Button, Image, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../css/HomePage.css';

// Asset Imports
import cosmosBannerImg from '../../assets/images/CPR_COSMOS/bannerImg.webp';
import parkHeightsBannerImg from '../../assets/images/CPR_PARK_HEIGHTS/heroImgTwo.webp';
import mangalaBannerImg from '../../assets/images/CPR_MANGALA/bannerImg.webp';
import video from '../../assets/videos/video_sample_three.mp4';

// Brochure Imports
import CPR_Park_Heights_Brochure from '../../assets/docs/CPR_Park_Heights_Brochure.pdf';
import CPR_Cosmos_Brochure from '../../assets/docs/CPR_Cosmos_Brochure.pdf'; // Ensure this exists
import CPR_Mangala_Brochure from '../../assets/docs/CPR_Mangala_Brochure.pdf'; // Ensure this exists
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';

const HomePage = () => {
    const navigate = useNavigate();

    // Mapping placeholders
    const parkHeightsImg = parkHeightsBannerImg;
    const cosmosImg = cosmosBannerImg;
    const mangalaImg = mangalaBannerImg;

    // --- STATES ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeProject, setActiveProject] = useState(""); 
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
    const [formStatus, setFormStatus] = useState({ type: '', message: '' });

    // --- HANDLERS ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleBrochureClick = (projectName) => {
        setActiveProject(projectName);
        setFormStatus({ type: '', message: '' }); 
        setIsModalOpen(true);
    };

    const handleBrochureSubmit = async (e) => {
        e.preventDefault();
        setFormStatus({ type: 'loading', message: 'Processing your request...' });

        // Identify which file and PHP to use based on activeProject
        let phpFile = '';
        let pdfFile = null;
        let downloadName = '';

        if (activeProject === "CPR Park Heights") {
            phpFile = 'park-heights-contact.php';
            pdfFile = CPR_Park_Heights_Brochure;
            downloadName = 'CPR_Park_Heights_Brochure.pdf';
        } else if (activeProject === "CPR Cosmos") {
            phpFile = 'cosmos-contact.php'; // Ensure this PHP exists
            pdfFile = CPR_Cosmos_Brochure;
            downloadName = 'CPR_Cosmos_Brochure.pdf';
        } else if (activeProject === "CPR Mangala") {
            phpFile = 'mangala-contact.php'; // Ensure this PHP exists
            pdfFile = CPR_Mangala_Brochure;
            downloadName = 'CPR_Mangala_Brochure.pdf';
        }

        const finalizeRequest = () => {
            setIsModalOpen(false);
            setFormData({ name: '', email: '', phone: '', message: '' });
            
            // Trigger the Download
            if (pdfFile) {
                const link = document.createElement('a');
                link.href = pdfFile;
                link.download = downloadName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        };

        try {
            const response = await fetch(phpFile, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    ...formData, 
                    interest: 'Brochure Download Request', 
                    source: `Home Page - ${activeProject}` 
                }),
            });
            const result = await response.json();

            if (result.status === 'success') {
                finalizeRequest();
            } else {
                setFormStatus({ type: 'error', message: result.message });
            }
        } catch (error) {
            // FALLBACK: Just like your Park Heights page logic
            finalizeRequest();
        }
    };

    return (
        <div className="homepage-wrapper">
            {/* --- SECTION 1: HERO --- */}
            <Container fluid className="hero-video-section p-0 w-100">
                <video src={video} autoPlay loop muted playsInline className="video" />
                <div className='overlay-text'>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <h1 className="display-2 fw-bold mb-3">CPR CONSTRUCTIONS</h1>
                        <h2 className="h4 text-uppercase mb-4 tracking-widest opacity-90">
                            Leading Property Developers & Best Apartments in Tirupati
                        </h2>
                        <p className="lead mb-4 mx-auto" style={{maxWidth: '800px'}}>
                            Delivering excellence through premium gated communities and luxury residential 
                            projects since 2008. Discover your dream home across our elite trilogy.
                        </p>
                        <Button 
                            variant="primary" 
                            className="px-5 py-3 rounded-pill bg-primary-blue border-0 shadow"
                            onClick={() => document.getElementById('park-heights').scrollIntoView({behavior: 'smooth'})}
                        >
                            EXPLORE PROJECTS
                        </Button>
                    </motion.div>
                </div>
            </Container>

            {/* --- SECTION 2: PARK HEIGHTS --- */}
            <section className="py-5 bg-white project-section" id="park-heights">
                <Container className="py-5">
                    <Row className="align-items-center flex-column-reverse flex-lg-row">
                        <Col lg={6} className="mt-4 mt-lg-0">
                            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                                <h6 className="text-primary-blue fw-bold text-uppercase mb-2">Modern Elevated Living</h6>
                                <h2 className="display-4 fw-bold mb-4">CPR Park Heights</h2>
                                <h3 className="project-subtitle h5 fw-bold text-dark mb-1">Premium High-Rise Apartments in Tirupati</h3>
                                <div className='d-flex gap-1'>
                                    <FontAwesomeIcon icon={faLocationDot} className='text-primary-blue'/>
                                    <h6 className="text-muted mb-3 mt-0">CPR Park Heights, beside Southern Spice Restaurant, near Avilala, Tirupati.</h6>
                                </div>
                                <p className="text-muted mb-4">
                                    Offering <strong>ultra-modern 2 & 3 BHK luxury flats</strong> designed for superior height and natural ventilation.
                                </p>
                                <div className='cta-buttons d-flex gap-3'>
                                    <Button variant="outline-dark" className="px-4 py-2 rounded-pill fw-bold" onClick={() => navigate('/cpr-park-heights')}>
                                        View Project Details
                                    </Button>
                                    <Button variant="outline-primary" className="px-4 py-2 rounded-pill fw-bold" onClick={() => handleBrochureClick("CPR Park Heights")}>
                                        Download Brochure
                                    </Button>
                                </div>
                            </motion.div>
                        </Col>
                        <Col lg={6}>
                            <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
                                <Image src={parkHeightsImg} alt="CPR Park Heights" className="w-100 rounded-4 shadow-lg project-img" />
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* --- SECTION 3: COSMOS --- */}
            <section className="py-5 bg-whitesmoke project-section" id="cosmos">
                <Container className="py-5">
                    <Row className="align-items-center flex-column-reverse flex-lg-row-reverse">
                        <Col lg={6} className="mt-4 mt-lg-0">
                            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                                <h6 className="text-primary-blue fw-bold text-uppercase mb-2">The Star Address</h6>
                                <h2 className="display-4 fw-bold mb-4">CPR Cosmos</h2>
                                <h3 className="project-subtitle h5 fw-bold text-dark mb-1">Luxury Gated Community on Renigunta Road</h3>
                                <div className='d-flex gap-1'>
                                    <FontAwesomeIcon icon={faLocationDot} className='text-primary-blue'/>
                                    <h6 className="text-muted mb-3 mt-0">CPR Cosmos, Ward No. 19, Postal Colony, Renigunta Road, Tirupati.</h6>
                                </div>
                                <p className="text-muted mb-4">
                                    Our flagship <strong>gated community in Tirupati</strong> featuring <strong>best-in-class 2 & 3 BHK luxury apartments</strong>.
                                </p>
                                <div className='cta-buttons d-flex gap-3'>
                                    <Button variant="outline-dark" className="px-4 py-2 rounded-pill fw-bold" onClick={() => navigate('/cpr-cosmos')}>
                                        View Project Details
                                    </Button>
                                    <Button variant="outline-primary" className="px-4 py-2 rounded-pill fw-bold" onClick={() => handleBrochureClick("CPR Cosmos")}>
                                        Download Brochure
                                    </Button>
                                </div>
                            </motion.div>
                        </Col>
                        <Col lg={6}>
                            <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
                                <Image src={cosmosImg} alt="CPR Cosmos" className="w-100 rounded-4 shadow-lg project-img" />
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* --- SECTION 4: MANGALA --- */}
            <section className="py-5 bg-white project-section" id="mangala">
                <Container className="py-5">
                    <Row className="align-items-center flex-column-reverse flex-lg-row">
                        <Col lg={6} className="mt-4 mt-lg-0">
                            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                                <h6 className="text-primary-blue fw-bold text-uppercase mb-2">New Era of Living</h6>
                                <h2 className="display-4 fw-bold mb-4">CPR Mangala</h2>
                                <h3 className="project-subtitle h5 fw-bold text-dark mb-1">Independent 2 BHK Flats at Mangalam, Tirupati</h3>
                                <div className='d-flex gap-1'>
                                    <FontAwesomeIcon icon={faLocationDot} className='text-primary-blue'/>
                                    <h6 className="text-muted mb-3 mt-0">CPR Mangala, Karakambadi Road, Near Mangalam, Tirupati.</h6>
                                </div>
                                <p className="text-muted mb-4">
                                    Experience <strong>independent 2 BHK flats with zero common walls</strong> and 45% open space. 
                                    Enjoy the advantage of <strong>No GST</strong> with prices starting from just <strong>₹37 Lakhs onwards</strong>, 
                                    making it the best value luxury housing near Karakambadi Road.
                                </p>
                                <div className='cta-buttons d-flex gap-3'>
                                    <Button variant="outline-dark" className="px-4 py-2 rounded-pill fw-bold" onClick={() => navigate('/cpr-mangala')}>
                                        View Project Details
                                    </Button>
                                    <Button variant="outline-primary" className="px-4 py-2 rounded-pill fw-bold" onClick={() => handleBrochureClick("CPR Mangala")}>
                                        Download Brochure
                                    </Button>
                                </div>
                            </motion.div>
                        </Col>
                        <Col lg={6}>
                            <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
                                <Image src={mangalaImg} alt="CPR Mangala" className="w-100 rounded-4 shadow-lg project-img" />
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* --- DYNAMIC BROCHURE MODAL --- */}
            <Modal show={isModalOpen} onHide={() => setIsModalOpen(false)} centered className="brochure-modal">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold text-primary-blue">Download Brochure</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 pt-0">
                    <p className="text-muted mb-4">Please enter your details to receive the official <strong>{activeProject}</strong> brochure.</p>
                    <form onSubmit={handleBrochureSubmit}>
                        <div className="mb-3">
                            <input name="name" type="text" className="form-control" placeholder="Full Name" value={formData.name} onChange={handleInputChange} required />
                        </div>
                        <Row className="g-2 mb-3">
                            <Col md={6}>
                                <input name="email" type="email" className="form-control" placeholder="Email Address" value={formData.email} onChange={handleInputChange} required />
                            </Col>
                            <Col md={6}>
                                <input name="phone" type="tel" className="form-control" placeholder="Phone Number" value={formData.phone} onChange={handleInputChange} required />
                            </Col>
                        </Row>
                        <div className="mb-3">
                            <textarea name="message" className="form-control" rows="3" placeholder="Message (Optional)" value={formData.message} onChange={handleInputChange}></textarea>
                        </div>
                        <Button 
                            type="submit" 
                            className="w-100 py-2 bg-primary-blue border-0 rounded-pill fw-bold shadow-sm text-white"
                            disabled={formStatus.type === 'loading'}
                        >
                            {formStatus.type === 'loading' ? 'PREPARING PDF...' : 'SUBMIT & DOWNLOAD'}
                        </Button>
                        {formStatus.message && (
                            <div className={`mt-3 small text-center ${formStatus.type === 'error' ? 'text-danger' : 'text-success'}`}>
                                {formStatus.message}
                            </div>
                        )}
                    </form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default HomePage;